import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { environment } from '../../../../environments/environment';
import { restoreCsrTheme } from '../../csr/csr-theme';

const API_URL = environment.apiHost;

interface PoolType { code: string; label: string; }
interface ExtConfig {
  id: number;
  extensionNumber: string;
  poolType: string;
  poolLabel: string;
  status: string;
  reservedByUserName: string | null;
  reservedAt: string | null;
}

@Component({
  selector: 'app-extension-config',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './extension-config.component.html',
  styleUrls: ['./extension-config.component.scss'],
})
export class ExtensionConfigComponent implements OnInit {

  extensions: ExtConfig[] = [];
  poolTypes: PoolType[] = [];
  loading = false;
  loadError = '';

  showForm = false;
  saving = false;
  saveError = '';
  form = { extensionNumber: '', password: '', poolType: '' };

  deletingIds = new Set<number>();

  get grouped(): { pool: PoolType; exts: ExtConfig[] }[] {
    return this.poolTypes.map(pt => ({
      pool: pt,
      exts: this.extensions.filter(e => e.poolType === pt.code),
    })).filter(g => g.exts.length > 0);
  }

  get ungrouped(): ExtConfig[] {
    const knownCodes = new Set(this.poolTypes.map(p => p.code));
    return this.extensions.filter(e => !knownCodes.has(e.poolType));
  }

  ngOnInit(): void {
    restoreCsrTheme();
    void this.load();
  }

  async load(): Promise<void> {
    this.loading = true;
    this.loadError = '';
    const token = localStorage.getItem('auth-token') ?? '';
    const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const [extRes, ptRes] = await Promise.all([
        fetch(`${API_URL}/api/v1/telephony/extensions/config`, { headers }),
        fetch(`${API_URL}/api/v1/telephony/extensions/pool-types`, { headers }),
      ]);
      const extJson = await extRes.json();
      const ptJson  = await ptRes.json();
      this.extensions = Array.isArray(extJson?.data) ? extJson.data : [];
      this.poolTypes  = Array.isArray(ptJson?.data)  ? ptJson.data  : [];
      if (!this.form.poolType && this.poolTypes.length) {
        this.form.poolType = this.poolTypes[0].code;
      }
    } catch (e: any) {
      this.loadError = e?.message ?? 'Failed to load';
    } finally {
      this.loading = false;
    }
  }

  async save(): Promise<void> {
    if (!this.form.extensionNumber.trim() || !this.form.password.trim() || !this.form.poolType) return;
    this.saving = true;
    this.saveError = '';
    const token = localStorage.getItem('auth-token') ?? '';
    try {
      const res = await fetch(`${API_URL}/api/v1/telephony/extensions/config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(this.form),
      });
      if (res.ok) {
        this.form = { extensionNumber: '', password: '', poolType: this.poolTypes[0]?.code ?? '' };
        this.showForm = false;
        await this.load();
      } else {
        const err: any = await res.json().catch(() => ({}));
        this.saveError = err?.message ?? `HTTP ${res.status}`;
      }
    } catch (e: any) {
      this.saveError = e?.message ?? 'Network error';
    } finally {
      this.saving = false;
    }
  }

  async deleteExt(ext: ExtConfig): Promise<void> {
    if (ext.status === 'RESERVED') return;
    this.deletingIds.add(ext.id);
    const token = localStorage.getItem('auth-token') ?? '';
    const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const res = await fetch(`${API_URL}/api/v1/telephony/extensions/config/${ext.id}`, {
        method: 'DELETE',
        headers,
      });
      if (res.ok) {
        await this.load();
      }
    } catch { /* silently ignore */ }
    finally { this.deletingIds.delete(ext.id); }
  }

  cancelForm(): void {
    this.showForm = false;
    this.saveError = '';
    this.form = { extensionNumber: '', password: '', poolType: this.poolTypes[0]?.code ?? '' };
  }
}
